import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import {
  FeatherArrowLeft,
  FeatherChevronRight,
  FeatherPlus,
} from "@subframe/core";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";

import { useNursery } from "@/contexts/NurseryContext";
import { useListFireExtinguisherChecksQueryControllerList } from "@/generated/fire-extinguisher-checks/fire-extinguisher-checks";
import { memberDisplayName } from "@/lib/member-display";

export const Route = createFileRoute(
  "/_authenticated/fire-extinguisher-checks/",
)({
  component: FireExtinguisherCheckHistoryPage,
});

function FireExtinguisherCheckHistoryPage() {
  const navigate = useNavigate();
  const { selectedNurseryId } = useNursery();

  const { data, isLoading } = useListFireExtinguisherChecksQueryControllerList(
    selectedNurseryId ?? "",
    {},
    { query: { enabled: !!selectedNurseryId } },
  );
  const checks = data?.data?.items ?? [];

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 overflow-y-auto bg-white">
      <div className="flex w-full flex-wrap items-center gap-6">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <div className="flex w-full items-start gap-2">
            <IconButton
              aria-label="Back"
              icon={<FeatherArrowLeft />}
              onClick={() =>
                navigate({ to: "/checklists", search: { tab: "logs" } })
              }
            />
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
              <span className="w-full whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
                Fire Extinguisher Visual Checks
              </span>
              <span className="text-body font-body text-subtext-color">
                Monthly visual inspections, newest first
              </span>
            </div>
          </div>
        </div>
        <div className="flex grow items-center justify-end gap-2">
          <Button
            variant="brand-primary"
            size="large"
            icon={<FeatherPlus />}
            onClick={() => navigate({ to: "/fire-extinguisher-checks/new" })}
          >
            New check
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Loader size="large" />
        </div>
      ) : checks.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center gap-2 py-12">
          <span className="text-body font-body text-subtext-color">
            No fire extinguisher checks have been recorded yet.
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-2">
          {checks.map((check) => (
            <Link
              key={check.id}
              to="/fire-extinguisher-checks/$checkId"
              params={{ checkId: check.id }}
              className="w-full"
            >
              <div className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3 hover:bg-neutral-50 transition-colors cursor-pointer">
                <div className="flex grow flex-col items-start gap-0.5">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {format(new Date(check.submittedAt), "d MMM yyyy, HH:mm")}
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Logged by{" "}
                    {check.recordedBy
                      ? memberDisplayName(check.recordedBy)
                      : "Unknown"}{" "}
                    · {check.itemCount}{" "}
                    {check.itemCount === 1 ? "extinguisher" : "extinguishers"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {check.failedCount > 0 ? (
                    <Badge variant="error">{check.failedCount} failed</Badge>
                  ) : (
                    <Badge variant="success">All passed</Badge>
                  )}
                  <FeatherChevronRight className="h-4 w-4 text-subtext-color" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
